// Shared types for the party import wizard (Contacts → Import).
import type {
  BillingFrequencyValue,
  PartyStatusValue,
  PartyTypeValue,
  PaymentMethodValue,
  SupplierTypeValue,
} from '@my/directory-api';

/** Supported upload formats, detected from the file extension. */
export type ImportFormat = 'json' | 'csv' | 'xlsx';

/** A raw parsed row — every value coerced to a trimmed string for uniform
 *  downstream handling (mapping / coercion happens later, per target field). */
export type RawRow = Record<string, string>;

/** Normalised output of {@link parseImportFile}, regardless of source format. */
export interface ParsedFile {
  format: ImportFormat;
  fileName: string;
  /** Column headers in first-seen order (union across rows for JSON). */
  headers: string[];
  rows: RawRow[];
  /** xlsx only — every sheet name, so the operator can switch sheets. */
  sheetNames?: string[];
  /** xlsx only — the sheet these rows came from. */
  activeSheet?: string;
}

/** Wizard step machine. */
export type WizardStep = 'upload' | 'map' | 'preview' | 'commit';

/** Hard cap for v1 — keeps the editable grid + per-row geocode manageable.
 *  Larger files get a friendly "split it" message; chunked commit comes later. */
export const IMPORT_MAX_ROWS = 1000;

// ── Mapping ──────────────────────────────────────────────────────────────────

/** Target fields a source column can map to (v1: party + primary address,
 *  no contacts, no supplier subtype, no per-row type). */
export type TargetField =
  | 'code'
  | 'name'
  | 'billingName'
  | 'taxNumber'
  | 'tags'
  | 'addrName'
  | 'fullAddress'
  | 'city'
  | 'postcode'
  | 'country'
  | 'latitude'
  | 'longitude';

/** Metadata driving the mapping UI + validation. */
export interface TargetFieldDef {
  id: TargetField;
  label: string;
  /** Must be mapped to a source column before Continue (code is NOT — it auto-generates). */
  required: boolean;
  group: 'party' | 'address';
  hint?: string;
}

export const TARGET_FIELDS: readonly TargetFieldDef[] = [
  { id: 'name', label: 'Name', required: true, group: 'party' },
  { id: 'code', label: 'Code', required: false, group: 'party', hint: 'Blank → auto-generated' },
  { id: 'billingName', label: 'Billing name', required: false, group: 'party', hint: 'Blank → uses Name' },
  { id: 'taxNumber', label: 'Tax number', required: false, group: 'party' },
  { id: 'tags', label: 'Tags', required: false, group: 'party', hint: 'Comma-separated' },
  { id: 'fullAddress', label: 'Address', required: true, group: 'address' },
  { id: 'addrName', label: 'Location name', required: false, group: 'address', hint: 'Blank → uses Name' },
  { id: 'city', label: 'City', required: false, group: 'address' },
  { id: 'postcode', label: 'Postcode', required: false, group: 'address' },
  { id: 'country', label: 'Country', required: true, group: 'address', hint: '2-letter ISO (IE / GB / XI)' },
  { id: 'latitude', label: 'Latitude', required: false, group: 'address' },
  { id: 'longitude', label: 'Longitude', required: false, group: 'address' },
] as const;

/** Target field → source header. Absent key = unmapped (ignored). */
export type ColumnMapping = Partial<Record<TargetField, string>>;

/** How to resolve each party's currency. */
export type CurrencyMode = 'fromCountry' | 'fixed';

/** Batch-level settings the operator picks once on the mapping step. */
export interface ImportDefaults {
  type: PartyTypeValue;
  status: PartyStatusValue;
  paymentMethod: PaymentMethodValue;
  billingFrequency: BillingFrequencyValue;
  currencyMode: CurrencyMode;
  /** Used when currencyMode = 'fixed'. */
  fixedCurrency: string;
}

export const DEFAULT_IMPORT_DEFAULTS: ImportDefaults = {
  type: 'Customer',
  status: 'Active',
  paymentMethod: 'Credit',
  billingFrequency: 'Monthly',
  currencyMode: 'fromCountry',
  fixedCurrency: 'EUR',
};

// ── Draft (materialised row, edited in preview, committed per-row) ───────────

/** Per-row commit outcome, populated by the commit step. */
export type DraftStatus = 'pending' | 'creating' | 'created' | 'skipped' | 'failed';

/**
 * One import row after mapping + defaults are applied. Mutable in the preview
 * grid; carries its own geocode + commit state. `rowRef` is a stable React /
 * result-correlation key (NOT the party code).
 */
export interface ImportDraft {
  rowRef: string;
  // Party
  code: string; // '' => auto-generated at commit
  name: string;
  billingName: string;
  type: PartyTypeValue;
  subtype: SupplierTypeValue | null;
  taxNumber: string;
  tags: string;
  status: PartyStatusValue;
  paymentMethod: PaymentMethodValue;
  billingFrequency: BillingFrequencyValue;
  currencyIsoCode: string;
  // Address
  addrName: string;
  fullAddress: string;
  city: string;
  postcode: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
  placeId: string;
  // Commit state
  status_: DraftStatus;
  /** Assigned party code (echoed when auto-generated) once created. */
  assignedCode?: string;
  /** Created ids — kept so a retry reuses them instead of duplicating. */
  createdPartyId?: string;
  createdPrimaryAddressId?: string;
  createdBillingAddressId?: string;
  error?: string;
}
